import { useEffect, useState, useRef } from "react";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import LaddaButton from "../../components/buttons/laddaButton";
import FormTextField from "../../components/textField/formTextField";
import SimpleSelect from "../../components/selects/simpleSelect";
import MultiSelect from "../../components/selects/multiSelect";
import FormCalendar from "../../components/calendar/formCalendar";
import FormRadioButton from "../../components/formRadioButton";
import ModalComponent from "../../components/modals";
import {
  callApiAsync,
  getCallCurrentUser,
  getNonBusinessDay,
  getProcessIdFromUser
} from "../../services/apiService";
import { getCookie } from "../../utils/getCookie";
 import FormTimeField from "../formTimeField";
import { getCurrentDate, formatDateOnly } from "../../services/dateService";
import { taskFullStates, taskStates } from "../../constants/selectData";
import { task_status_ENUM } from "../../constants/enums";

const AddTaskModal = ({
  open,
  handleClose,
  onSubmit,
  onSaveAttemptEndRef,
  initialData = null,
  assignableList = [],
  disabled = false,
  loadNotificationList = () => {}
}) => {
  const [processList, setProcessList] = useState([]);
  const [userName, setUserName] = useState("");
  const [disabledDates, setDisabledDates] = useState([]);
  const prevOpenRef = useRef(false);
  const hasSetDefaultAssigneeRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [processIdByUserId, setProcessIdByUserId] = useState({});
  const [processNamesDisplay, setProcessNamesDisplay] = useState("");
  const [assigneeOptions, setAssigneeOptions] = useState([]);

  useEffect(() => {
    const list = Array.isArray(assignableList) ? [...assignableList] : [];
    const empId = initialData?.employee_in_charge ?? initialData?.employee;
    const empName =
      initialData?.employer_name ||
      initialData?.employee_name ||
      initialData?.visible_as_user_name;
    if (
      empId != null &&
      empId !== "" &&
      !list.some((o) => String(o?.value) === String(empId))
    ) {
      list.push({
        value: empId,
        name: empName || `Usuario ${empId}`
      });
    }
    setAssigneeOptions(list);
  }, [assignableList, initialData]);

  const hhmmToMinutes = (s) => {
    if (!s) return null;
    const [h, m] = s.split(":").map(Number);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };

  const schema = Yup.object().shape({
    description: Yup.string().required("La descripcion es obligatoria"),
    process: Yup.mixed().nullable(),
    end_date: Yup.date().nullable().required("La fecha de fin es obligatoria"),
    task_continuous: Yup.string().required("Este campo es obligatorio"),
    employee_in_charge: Yup.mixed().test(
      "at-least-one",
      "Selecciona al menos un empleado",
      (v) =>
        Array.isArray(v) ? v.length >= 1 : v != null && String(v).trim() !== ""
    ),
    task_status: Yup.number().required("El estado es obligatorio"),
    start_hour: Yup.string()
      .required("La hora de inicio es obligatoria")
      .matches(/^\d{2}:\d{2}$/, "Formato inválido (HH:mm)"),
    end_hour: Yup.string()
      .required("La hora de fin es obligatoria")
      .matches(/^\d{2}:\d{2}$/, "Formato inválido (HH:mm)")
      .test(
        "is-after-start",
        "La hora fin debe ser mayor que la hora inicio",
        function (value) {
          const s = this.parent.start_hour;
          const sm = hhmmToMinutes(s);
          const em = hhmmToMinutes(value);
          if (sm == null || em == null) return false;
          return em > sm;
        }
      )
  });

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      description: "",
      process: "",
      start_date: null,
      end_date: null,
      task_continuous: "",
      employee_in_charge: [],
      task_status: null,
      start_hour: "06:30",
      end_hour: "18:00"
    }
  });

  const startDate = watch("start_date");
  const endDate = watch("end_date");
  const task_continuous = watch("task_continuous");
  const startHour = watch("start_hour");
  const endHour = watch("end_hour");
  const assigneeUser = watch("employee_in_charge");

  const isEditing = !!initialData;

  const getNonBusinessDaysData = async () => {
    await callApiAsync(
      () => getNonBusinessDay(),
      (data) => {
        setDisabledDates(data);
      }
    );
  };

  useEffect(() => {
    if (startDate && endDate && endDate < startDate) {
      setValue("end_date", startDate, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [startDate, endDate, setValue]);

  useEffect(() => {
    const sm = hhmmToMinutes(startHour);
    const em = hhmmToMinutes(endHour);
    const lastSlotMinutes = 23 * 60 + 45;
    if (sm != null && em != null && em <= sm) {
      const rawNext = sm + 15;
      const next = Math.min(rawNext, lastSlotMinutes);
      const h = String(Math.floor(next / 60)).padStart(2, "0");
      const m = String(next % 60).padStart(2, "0");
      setValue("end_hour", `${h}:${m}`, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [startHour, setValue]);

  useEffect(() => {
    getNonBusinessDaysData();
  }, []);

  useEffect(() => {
    if (task_continuous === "0") {
      setValue("start_date", "");
    }
  }, [task_continuous, setValue]);

  useEffect(() => {
    const ids = Array.isArray(assigneeUser)
      ? assigneeUser.filter((id) => id != null && String(id).trim() !== "")
      : assigneeUser != null && String(assigneeUser).trim() !== ""
      ? [assigneeUser]
      : [];

    if (disabled || ids.length === 0) {
      setProcessList([]);
      setProcessIdByUserId({});
      setProcessNamesDisplay("");
      return;
    }

    if (ids.length === 1) {
      callApiAsync(
        () =>
          getProcessIdFromUser(
            disabled ? 0 : ids[0],
            disabled ? true : false
          ),
        (data) => {
          setProcessList(data.processes || []);
          setProcessIdByUserId({ [ids[0]]: data.primary_process_id ?? data.processes?.[0]?.value ?? null });
          setProcessNamesDisplay("");
          const processId = data.primary_process_id ?? data.processes?.[0]?.value ?? "";
          setValue("process", processId != null && processId !== "" ? processId : "", {
            shouldValidate: true,
            shouldDirty: true
          });
        }
      );
      return;
    }

    Promise.all(ids.map((id) => getProcessIdFromUser(id, false)))
      .then((responses) => {
        const byId = {};
        const namesSet = new Set();
        responses.forEach((res, i) => {
          const data = res?.data ?? res;
          const uid = ids[i];
          const processId = data.primary_process_id ?? data.processes?.[0]?.value ?? null;
          byId[uid] = processId;
          const name = data.processes?.find((p) => p.value === processId)?.name ?? data.processes?.[0]?.name;
          if (name) namesSet.add(name);
        });
        setProcessIdByUserId(byId);
        setProcessNamesDisplay(Array.from(namesSet).join(", "));
        const allProcesses = [];
        responses.forEach((res) => {
          const data = res?.data ?? res;
          (data.processes || []).forEach((p) => {
            if (!allProcesses.some((x) => x.value === p.value)) allProcesses.push(p);
          });
        });
        setProcessList(allProcesses);
        const uniqueProcessIds = [...new Set(Object.values(byId).filter(Boolean))];
        setValue("process", uniqueProcessIds, { shouldValidate: true, shouldDirty: true });
      })
      .catch(() => {
        setProcessIdByUserId({});
        setProcessNamesDisplay("");
        setProcessList([]);
      });
  }, [assigneeUser, disabled, setValue]);

  useEffect(() => {
    const userSesion = getCookie("sesion");
    if (!userSesion) return;
    const userId = userSesion.id_usuario;

    const pickDefaultAssignee = (loggedId) => {
      const opts = Array.isArray(assigneeOptions) ? assigneeOptions : [];
      const hasSelf = opts.some((o) => String(o?.value) === String(loggedId));
      if (hasSelf) return String(loggedId);
      const first = opts[0]?.value;
      return first != null ? String(first) : String(loggedId);
    };

    const fetchUser = async () => {
      try {
        const response = await getCallCurrentUser(userId);
        const data = response.data;
        const name = data?.nombre || "Usuario";
        setUserName(name);

        if (open && !initialData) {
          const defaultId = pickDefaultAssignee(userId);
          setValue(
            "employee_in_charge",
            defaultId != null ? [String(defaultId)] : [],
            { shouldValidate: true, shouldDirty: true }
          );
        }
      } catch (error) {
        console.error("Error al obtener usuario en modal:", error);
      }
    };

    fetchUser();
  }, [open, initialData, setValue, assigneeOptions]);

  const mapInitialToForm = (task) => {
    const userSesion = getCookie("sesion");
    const loggedUserId = userSesion?.id_usuario;

    if (!task) {
      return {
        description: "",
        process: null,
        start_date: null,
        end_date: null,
        task_continuous: "",
        employee_in_charge: [],
        task_status: null,
        start_hour: "",
        end_hour: ""
      };
    }

    const empId = task.employee_in_charge ?? task.employee ?? loggedUserId ?? "";
    const rawState = Number(task.raw_state ?? task.state);
    const editableState =
      rawState === task_status_ENUM.IN_PROCESS
        ? task_status_ENUM.IN_PROCESS
        : task_status_ENUM.TO_DO;
    return {
      description: task.description || task.title || "",
      process: task.processId != null ? task.processId : "",
      start_date: task.initial_date ? new Date(task.initial_date) : null,
      end_date: task.end_date ? new Date(task.end_date) : null,
      task_continuous: task.is_continue === true ? "1" : "0",
      employee_in_charge: String(empId),
      task_status: editableState,
      start_hour:
        (task.today_start_hour || task.start_hour || "").slice(0, 5) || "",
      end_hour: (task.today_end_hour || task.end_hour || "").slice(0, 5) || ""
    };
  };

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    if (justOpened) {
      reset(mapInitialToForm(initialData));
    } else {
      setIsSubmitting(false);
    }
    prevOpenRef.current = open;
  }, [open, initialData, reset]);

  useEffect(() => {
    if (!onSaveAttemptEndRef) return;
    onSaveAttemptEndRef.current = () => setIsSubmitting(false);
    return () => {
      onSaveAttemptEndRef.current = null;
    };
  }, [onSaveAttemptEndRef]);

  const toHHMMSS = (hhmm) => (hhmm ? `${hhmm}:00` : null);

  const handleSubmitClick = () => {
    setIsSubmitting(true);
    handleSubmit(handleSave, () => setIsSubmitting(false))();
  };

  const handleSave = async (data) => {
    if (isSubmitting) return;

    const clientNow = getCurrentDate();
    const [clientDay] = clientNow.split(" ");

    const employees = Array.isArray(data.employee_in_charge)
      ? data.employee_in_charge.filter((id) => id != null && String(id).trim() !== "")
      : data.employee_in_charge != null && String(data.employee_in_charge).trim() !== ""
      ? [data.employee_in_charge]
      : [];

    const isMultipleCreate =
      !initialData?.id && employees.length > 1;

    const adaptedTask = {
      id: initialData?.id,
      title: data.description,
      description: data.description,
      process: data.process,
      start_date: data.start_date ? formatDateOnly(data.start_date) : null,
      end_date: data.end_date ? formatDateOnly(data.end_date) : null,

      task_continuous: data.task_continuous,
      employee: isMultipleCreate ? undefined : (employees[0] ?? data.employee_in_charge),
      employee_in_charge: isMultipleCreate ? employees : (employees[0] ?? data.employee_in_charge),
      task_status: data.task_status,

      start_hour: toHHMMSS(data.start_hour),
      end_hour: toHHMMSS(data.end_hour),

      client_now: clientNow,
      client_day: clientDay
    };

    if (isMultipleCreate) {
      adaptedTask.employeesWithProcess = employees.map((id) => ({
        employee_in_charge_id: Number(id),
        process_id: processIdByUserId[id] ?? processIdByUserId[String(id)] ?? null
      }));
    }

    onSubmit(adaptedTask);
    loadNotificationList();
  };

  return (
    <ModalComponent
      open={open}
      handleClose={handleClose}
      title={!disabled && (initialData?.id ? "Editar Tarea" : "Crear Nueva Tarea" )}
      maxWidth="sm"
      actions={
        !disabled && (
          <div className="HContainer j-center w-complete">
            <LaddaButton
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              onClick={handleSubmitClick}
            >
              <p className="button_text">
                {initialData?.id ? "Editar" : "Crear"} Tarea
              </p>
            </LaddaButton>
          </div>
        )
      }
    >
      <form
        onSubmit={handleSubmit(handleSave)}
        className="HContainer j-center w-complete"
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <p className="blueLabelForm">Descripción:</p>
            <FormTextField
              name="description"
              control={control}
              isTextArea={true}
              error={errors?.description}
              maxLength={500}
              rows={2}
              width={"100%"}
              margin={"0"}
              disabled={disabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="blueLabelForm">¿Es una tarea continua?</p>
            <FormRadioButton
              name="task_continuous"
              control={control}
              options={[
                { label: "Sí", value: "1" },
                { label: "No", value: "0" }
              ]}
              error={errors.task_continuous}
              disabled={isEditing || disabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="blueLabelForm">
              Empleado a cargo
              {!isEditing && (
                <span style={{ fontSize: 12, fontWeight: 400, color: "#666" }}>
             
                </span>
              )}
            </p>
            {isEditing ? (
              <SimpleSelect
                name="employee_in_charge"
                control={control}
                error={errors?.employee_in_charge}
                options={assigneeOptions}
                margin={"0"}
                disabled={disabled}
              />
            ) : (
              <MultiSelect
                name="employee_in_charge"
                control={control}
                options={assigneeOptions}
                error={errors?.employee_in_charge}
                margin={"0"}
                disabled={disabled}
                isMultiple={true}
              />
            )}
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="blueLabelForm">Fecha de Inicio</p>
            <FormCalendar
              name="start_date"
              control={control}
              width={"100%"}
              error={errors.start_date}
              defaultValue={null}
              maxDate={endDate || undefined}
              margin={"0"}
              disabled={task_continuous === "0" || disabled}
              disableWeekends={true}
              disabledDates={disabledDates}
              //minDate={new Date()}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="blueLabelForm">Fecha de Fin</p>
            <FormCalendar
              name="end_date"
              control={control}
              width={"100%"}
              error={errors.end_date}
              defaultValue={null}
              minDate={startDate || undefined}
              margin={"0"}
              disabled={disabled}
              disableWeekends={true}
              disabledDates={disabledDates}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="blueLabelForm">Hora inicio</p>
            <FormTimeField
              name="start_hour"
              control={control}
              label=""
              start="00:00"
              end="23:45"
              step={15}
              disabled={disabled}
              error={errors.start_hour}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="blueLabelForm">Hora fin</p>
            <FormTimeField
              name="end_hour"
              control={control}
              label=""
              start="00:00"
              end="23:45"
              step={15}
              disabled={disabled}
              error={errors.end_hour}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="blueLabelForm">Proceso principal</p>
            {(() => {
              const ids = Array.isArray(assigneeUser)
                ? assigneeUser.filter((id) => id != null && String(id).trim() !== "")
                : assigneeUser != null && String(assigneeUser).trim() !== ""
                ? [assigneeUser]
                : [];
              if (ids.length > 1 && processList.length > 0) {
                return (
                  <MultiSelect
                    name="process"
                    control={control}
                    options={processList}
                    error={errors?.process}
                    margin={"0"}
                    disabled={true}
                    isMultiple={true}
                  />
                );
              }
              return (
                <SimpleSelect
                  name="process"
                  control={control}
                  error={errors?.process}
                  options={processList}
                  disabled={true}
                  margin={"0"}
                />
              );
            })()}
          </Grid>

          <Grid item xs={12} sm={6}>
            <p className="blueLabelForm">Estado de Tarea</p>
            <SimpleSelect
              name="task_status"
              control={control}
              error={errors?.task_status}
              options={disabled === true ? taskFullStates : taskStates}
              disabled={disabled}
              margin={"0"}
            />
          </Grid>
        </Grid>
      </form>
    </ModalComponent>
  );
};

export default AddTaskModal;
